import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, qs } from '../api';
import { Icon } from '../components/icons';
import { DeleteButton, Empty, ErrorBox, Field, ItemStatusBadge, Loading, Modal, PageHead } from '../components/ui';
import { useT } from '../i18n';
import type { Item, ItemStatus, Kit } from '../types';

export const CATEGORY_SUGGESTIONS = [
  'IR receiver',
  'IR transmitter',
  'IR radiator',
  'Headset',
  'Interpreter console',
  'Delegate unit',
  'Chairman unit',
  'Central unit',
  'Booth',
  'DS24T stand',
  'Microphone',
  'Speaker',
  'Cable',
  'Other',
];

interface Summary {
  category: string;
  total: number;
  available: number;
  out: number;
  in_repair: number;
  lost: number;
}

export interface LabelSpec {
  code: string;
  name: string;
}
/** Hand a list of labels to the print page. */
export function useOpenLabels() {
  const navigate = useNavigate();
  return (labels: LabelSpec[]) => {
    try {
      sessionStorage.setItem('tcdesk.labels', JSON.stringify(labels));
    } catch {
      /* ignore */
    }
    navigate('/labels', { state: { labels } });
  };
}

export function EquipmentPage() {
  const t = useT();
  const navigate = useNavigate();
  const openLabels = useOpenLabels();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<ItemStatus | ''>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<'add' | 'bulk' | null>(null);

  const summary = useQuery({
    queryKey: ['items', 'summary'],
    queryFn: async () => (await api.get<Summary[]>('/items/summary')).sort((a, b) => a.category.localeCompare(b.category)),
  });
  const items = useQuery({
    queryKey: ['items', { search, category, status }],
    queryFn: () => api.get<Item[]>(`/items${qs({ search, category, status })}`),
  });

  const list = items.data ?? [];
  const allSelected = list.length > 0 && list.every((i) => selected.has(i.id));
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(list.map((i) => i.id)));
  const totals = useMemo(
    () => (summary.data ?? []).reduce((a, s) => ({ total: a.total + s.total, available: a.available + s.available, out: a.out + s.out, problem: a.problem + s.in_repair + s.lost }), { total: 0, available: 0, out: 0, problem: 0 }),
    [summary.data],
  );

  const printSelected = () => {
    const chosen = list.filter((i) => selected.has(i.id));
    openLabels((chosen.length ? chosen : list).map((i) => ({ code: i.qrCode, name: i.name })));
  };

  return (
    <>
      <PageHead
        title={t('equipment.title')}
        sub={t('equipment.sub')}
        actions={
          <>
            <button className="btn" onClick={printSelected} disabled={!list.length}>
              <Icon.print />
              {selected.size ? t('equipment.printSelected', { n: selected.size }) : t('equipment.printShown', { n: list.length })}
            </button>
            <button className="btn" onClick={() => setModal('bulk')}>
              <Icon.plus />
              {t('equipment.addMany')}
            </button>
            <button className="btn btn-primary" onClick={() => setModal('add')}>
              <Icon.plus />
              {t('equipment.addItem')}
            </button>
          </>
        }
      />

      <div className="grid grid-4" style={{ marginBottom: 16 }}>
        <div className="card stat"><div className="label">{t('equipment.statTotal')}</div><div className="value">{totals.total}</div></div>
        <div className="card stat"><div className="label">{t('itemStatus.available')}</div><div className="value">{totals.available}</div></div>
        <div className="card stat"><div className="label">{t('equipment.statOut')}</div><div className="value">{totals.out}</div></div>
        <div className="card stat"><div className="label">{t('equipment.statProblem')}</div><div className="value" style={{ color: totals.problem ? 'var(--red-ink)' : undefined }}>{totals.problem}</div></div>
      </div>

      {summary.data && summary.data.length > 0 && (
        <div className="card table-wrap" style={{ marginBottom: 16 }}>
          <table className="table">
            <thead>
              <tr>
                <th>{t('equipment.category')}</th>
                <th className="right">{t('equipment.statTotal')}</th>
                <th className="right">{t('itemStatus.available')}</th>
                <th className="right">{t('itemStatus.out')}</th>
                <th className="right">{t('itemStatus.in_repair')}</th>
                <th className="right">{t('itemStatus.lost')}</th>
              </tr>
            </thead>
            <tbody>
              {summary.data.map((s) => (
                <tr key={s.category} className="clickable" onClick={() => setCategory(s.category === category ? '' : s.category)}>
                  <td><strong>{s.category}</strong>{category === s.category && <span className="badge badge-ink" style={{ marginLeft: 8 }}>{t('common.filtered')}</span>}</td>
                  <td className="right num">{s.total}</td>
                  <td className="right num">{s.available}</td>
                  <td className="right num">{s.out}</td>
                  <td className="right num">{s.in_repair || ''}</td>
                  <td className="right num" style={{ color: s.lost ? 'var(--red-ink)' : undefined }}>{s.lost || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="toolbar">
        <input className="input search" type="search" placeholder={t('equipment.search')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('equipment.search')} />
        <select className="select" value={category} onChange={(e) => setCategory(e.target.value)} aria-label={t('equipment.category')}>
          <option value="">{t('equipment.allCategories')}</option>
          {(summary.data ?? []).map((s) => (
            <option key={s.category} value={s.category}>{s.category}</option>
          ))}
        </select>
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as ItemStatus | '')} aria-label={t('common.status')}>
          <option value="">{t('equipment.allStatuses')}</option>
          {(['available', 'out', 'in_repair', 'lost', 'retired'] as ItemStatus[]).map((s) => (
            <option key={s} value={s}>{t(`itemStatus.${s}`)}</option>
          ))}
        </select>
        <span className="muted small">{t('common.shown', { n: list.length })}</span>
      </div>

      <div className="card table-wrap">
        {items.isLoading ? (
          <Loading />
        ) : items.error ? (
          <div className="card-body"><ErrorBox error={items.error} /></div>
        ) : list.length === 0 ? (
          <Empty title={t('equipment.emptyTitle')} text={t('equipment.emptyText')} action={<button className="btn btn-primary" onClick={() => setModal('bulk')}>{t('equipment.addMany')}</button>} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th className="check"><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label={t('common.selectAll')} /></th>
                <th>{t('equipment.code')}</th>
                <th>{t('equipment.name')}</th>
                <th>{t('equipment.category')}</th>
                <th>{t('equipment.brandModel')}</th>
                <th>{t('equipment.serial')}</th>
                <th>{t('equipment.case')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((i) => (
                <tr key={i.id} className="clickable" onClick={() => navigate(`/equipment/${i.id}`)}>
                  <td className="check" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} aria-label={i.qrCode} />
                  </td>
                  <td className="mono nowrap">{i.qrCode}</td>
                  <td><Link to={`/equipment/${i.id}`} onClick={(e) => e.stopPropagation()}>{i.name}</Link></td>
                  <td>{i.category}</td>
                  <td className="muted">{[i.brand, i.model].filter(Boolean).join(' ')}</td>
                  <td className="mono muted">{i.serialNumber}</td>
                  <td className="muted">{i.kitName}</td>
                  <td>
                    <ItemStatusBadge status={i.status} />
                    {i.outOnEvent && <div className="small muted">{i.outOnEvent}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal === 'add' && <ItemFormModal onClose={() => setModal(null)} />}
      {modal === 'bulk' && <BulkAddModal onClose={() => setModal(null)} />}
    </>
  );
}

export function ItemFormModal({ item, onClose }: { item?: Item; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: item?.name ?? '',
    category: item?.category ?? '',
    brand: item?.brand ?? '',
    model: item?.model ?? '',
    serialNumber: item?.serialNumber ?? '',
    qrCode: item?.qrCode ?? '',
    purchaseDate: item?.purchaseDate ?? '',
    purchasePrice: item?.purchasePrice ?? '',
    notes: item?.notes ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        category: f.category,
        brand: f.brand || null,
        model: f.model || null,
        serialNumber: f.serialNumber || null,
        purchaseDate: f.purchaseDate || null,
        purchasePrice: f.purchasePrice === '' ? null : Number(f.purchasePrice),
        notes: f.notes || null,
        ...(f.qrCode && f.qrCode !== item?.qrCode ? { qrCode: f.qrCode } : {}),
      };
      return item ? api.patch<Item>(`/items/${item.id}`, body) : api.post<Item>('/items', body);
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['item', saved.id] });
      onClose();
      if (!item) navigate(`/equipment/${saved.id}`);
    },
  });
  return (
    <Modal
      title={item ? t('equipment.editItem') : t('equipment.addItem')}
      onClose={onClose}
      footer={
        <>
          {item && (
            <DeleteButton
              name={`${item.qrCode} · ${item.name}`}
              path={`/items/${item.id}`}
              blockedCode="item_has_history"
              confirmText={t('del.itemText')}
              alternative={
                item.status !== 'out' && item.status !== 'retired'
                  ? { label: t('del.retireInstead'), run: () => api.patch(`/items/${item.id}`, { status: 'retired' }) }
                  : undefined
              }
              onDone={() => {
                onClose();
                navigate('/equipment');
                qc.invalidateQueries();
              }}
            />
          )}
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !f.name || !f.category}>{t('common.save')}</button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <div className="form-grid">
        <Field label={t('equipment.name')} full htmlFor="i-name">
          <input id="i-name" className="input" value={f.name} onChange={set('name')} placeholder="Brähler INFRACOM IR receiver" autoFocus />
        </Field>
        <Field label={t('equipment.category')} htmlFor="i-cat">
          <input id="i-cat" className="input" list="cat-list" value={f.category} onChange={set('category')} />
          <datalist id="cat-list">{CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label={t('equipment.serial')} htmlFor="i-serial">
          <input id="i-serial" className="input" value={f.serialNumber} onChange={set('serialNumber')} />
        </Field>
        <Field label={t('equipment.brand')} htmlFor="i-brand">
          <input id="i-brand" className="input" value={f.brand} onChange={set('brand')} />
        </Field>
        <Field label={t('equipment.model')} htmlFor="i-model">
          <input id="i-model" className="input" value={f.model} onChange={set('model')} />
        </Field>
        <Field label={t('equipment.purchaseDate')} htmlFor="i-pd">
          <input id="i-pd" className="input" type="date" value={f.purchaseDate} onChange={set('purchaseDate')} />
        </Field>
        <Field label={t('equipment.purchasePrice')} htmlFor="i-pp">
          <input id="i-pp" className="input" type="number" min="0" step="0.01" value={f.purchasePrice} onChange={set('purchasePrice')} />
        </Field>
        <Field label={t('equipment.code')} hint={t('equipment.codeHint')} full htmlFor="i-code">
          <input id="i-code" className="input mono" value={f.qrCode} onChange={set('qrCode')} placeholder="TC-……" />
        </Field>
        <Field label={t('common.notes')} full htmlFor="i-notes">
          <textarea id="i-notes" className="textarea" value={f.notes} onChange={set('notes')} />
        </Field>
      </div>
    </Modal>
  );
}

function BulkAddModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const openLabels = useOpenLabels();
  const kits = useQuery({ queryKey: ['kits'], queryFn: () => api.get<Kit[]>('/kits') });
  const [f, setF] = useState({ count: '50', name: '', category: '', brand: '', model: '', kitId: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const [created, setCreated] = useState<Item[] | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.post<Item[]>('/items/bulk', {
        count: Number(f.count),
        name: f.name,
        category: f.category,
        brand: f.brand || null,
        model: f.model || null,
        kitId: f.kitId || undefined,
      }),
    onSuccess: (items) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['kits'] });
      setCreated(items);
    },
  });
  const count = Number(f.count);
  if (created) {
    return (
      <Modal
        title={t('equipment.bulkDoneTitle')}
        onClose={onClose}
        footer={
          <>
            <button className="btn" onClick={onClose}>{t('common.close')}</button>
            <button className="btn btn-primary" onClick={() => openLabels(created.map((i) => ({ code: i.qrCode, name: i.name })))}>
              <Icon.print />
              {t('equipment.printTheseLabels', { n: created.length })}
            </button>
          </>
        }
      >
        <div className="alert alert-ok">{t('equipment.bulkDone', { n: created.length, name: created[0]?.name ?? '' })}</div>
        <p className="muted">{t('equipment.bulkDoneHint')}</p>
      </Modal>
    );
  }
  return (
    <Modal
      title={t('equipment.addMany')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !f.name || !f.category || !(count >= 1 && count <= 500)}>
            {t('equipment.createN', { n: count >= 1 ? count : 0 })}
          </button>
        </>
      }
    >
      <p className="muted" style={{ margin: 0 }}>{t('equipment.bulkHint')}</p>
      <ErrorBox error={save.error} />
      <div className="form-grid">
        <Field label={t('equipment.howMany')} hint="1–500" htmlFor="b-count">
          <input id="b-count" className="input" type="number" min="1" max="500" value={f.count} onChange={set('count')} />
        </Field>
        <Field label={t('equipment.category')} htmlFor="b-cat">
          <input id="b-cat" className="input" list="cat-list-b" value={f.category} onChange={set('category')} placeholder="IR receiver" />
          <datalist id="cat-list-b">{CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label={t('equipment.name')} full htmlFor="b-name">
          <input id="b-name" className="input" value={f.name} onChange={set('name')} placeholder="Brähler INFRACOM IR receiver" />
        </Field>
        <Field label={t('equipment.brand')} htmlFor="b-brand">
          <input id="b-brand" className="input" value={f.brand} onChange={set('brand')} />
        </Field>
        <Field label={t('equipment.model')} htmlFor="b-model">
          <input id="b-model" className="input" value={f.model} onChange={set('model')} />
        </Field>
        <Field label={t('equipment.putInCase')} hint={t('equipment.putInCaseHint')} full htmlFor="b-kit">
          <select id="b-kit" className="select" value={f.kitId} onChange={set('kitId')}>
            <option value="">{t('equipment.noCase')}</option>
            {(kits.data ?? []).map((k) => (
              <option key={k.id} value={k.id}>{k.name} ({k.qrCode})</option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
